import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { useTranslation } from '../../hooks/useTranslation';
import { useNotification } from '../../hooks/useNotification';
import { ordersService } from '../../services/ordersService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

interface CancelOrderButtonProps {
  orderId: string;
  orderStatus: string;
  orderPaid?: boolean;
  onCancelSuccess?: () => void;
}

export const CancelOrderButton: React.FC<CancelOrderButtonProps> = ({
  orderId,
  orderStatus,
  orderPaid = false,
  onCancelSuccess,
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const [isCancelling, setIsCancelling] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const isCancelled = orderStatus === 'cancelled';
  const isDelivered = orderStatus === 'delivered';
  // Cannot cancel if already cancelled, delivered, or paid
  const canCancel = !isCancelled && !isDelivered && !orderPaid;

  const handleCancelClick = () => {
    if (!canCancel) {
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmCancel = async () => {
    setShowConfirmDialog(false);
    setIsCancelling(true);
    try {
      // First, cancel the order itself
      await ordersService.updateOrderStatus(orderId, 'cancelled');

      // Then, fetch and cancel all suborders if this is a parent order
      try {
        // Fetch orders with pagination to ensure we get all suborders
        let suborders: any[] = [];
        let page = 1;
        const perPage = 1000;
        let hasMore = true;

        while (hasMore) {
          const ordersResponse = await ordersService.getOrders({
            pagination: { page, perPage },
            sort: { field: 'date', order: 'DESC' },
            filter: {},
          });

          const pageSuborders = (ordersResponse.data || []).filter(
            (o: any) => o.parentOrderId === orderId
          );
          suborders = suborders.concat(pageSuborders);

          // Check if we've fetched all orders
          if (
            ordersResponse.data.length < perPage ||
            suborders.length >= (ordersResponse.total || 0)
          ) {
            hasMore = false;
          } else {
            page++;
          }
        }

        // Cancel all suborders
        if (suborders.length > 0) {
          const cancelPromises = suborders.map((suborder) =>
            ordersService.updateOrderStatus(suborder.id.toString(), 'cancelled')
          );
          await Promise.all(cancelPromises);
        }
      } catch (suborderError) {
        console.error('Error cancelling suborders:', suborderError);
        // Don't fail the whole operation if suborder cancellation fails
        // The parent order is already cancelled
      }

      notify(
        translate(
          'pos.orders.messages.orderCancelled',
          'Order cancelled successfully'
        ),
        { type: 'success' }
      );

      // Call success callback if provided
      if (onCancelSuccess) {
        onCancelSuccess();
      } else {
        // Otherwise navigate back to orders list
        navigate('/orders');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      notify(
        translate(
          'pos.orders.messages.cancelError',
          'Failed to cancel order. Please try again.'
        ),
        { type: 'error' }
      );
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        onClick={handleCancelClick}
        disabled={!canCancel || isCancelling}
        className="flex items-center gap-2"
      >
        <X className="h-4 w-4" />
        {isCancelling
          ? translate('pos.common.action.saving', 'Cancelling...')
          : translate('pos.orders.actions.cancel', 'Cancel Order')}
      </Button>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate(
                'pos.orders.messages.cancel_confirm_title',
                'Cancel Order'
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate(
                'pos.orders.messages.cancel_confirm_message',
                'Are you sure you want to cancel this order?'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>
              {translate('pos.common.action.close', 'Close')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              className="bg-red-600 hover:bg-red-700"
            >
              {translate('pos.orders.actions.cancel', 'Cancel Order')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
